import React, { useEffect, useState, useCallback } from "react";
import {
  Autocomplete,
  List,
  ListItem,
  ListItemText,
  Typography,
  Card,
  CardContent,
  Checkbox,
  Box,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Snackbar,
  Alert,
  Stack,
  Chip,
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import BusinessIcon from "@mui/icons-material/Business";
import AddTaskIcon from "@mui/icons-material/AddTask";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import dayjs from "dayjs";
import { useAuth } from "../../providers/AuthProvider";
import api, { API_BASE, isCancel } from "../../services/api/axios";
import { BRAND_BLUE, BRAND_GREEN } from "../../theme/colors";

const FIELD_BG = "rgba(255,255,255,0.96)";

const cardBaseSx = {
  width: "100%",
  height: "100%",
  border: "1px solid rgba(0,51,108,0.1)",
  borderRadius: "14px",
  boxShadow: "0 1px 4px rgba(15,23,42,0.05), 0 8px 24px rgba(15,23,42,0.07)",
  background: "#ffffff",
  overflow: "hidden",
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "3px",
    background: BRAND_BLUE,
  },
};

const modalFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
    backgroundColor: FIELD_BG,
    minHeight: 44,
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: BRAND_BLUE,
      borderWidth: 1.5,
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: BRAND_BLUE,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: BRAND_BLUE,
      borderWidth: 2,
    },
  },
  "& .MuiInputBase-input::placeholder": {
    color: BRAND_BLUE,
    opacity: 1,
    fontStyle: "normal",
  },
};

const modalFieldLabelSx = {
  color: "#344863",
  fontSize: "0.85rem",
  fontWeight: 700,
  mb: 0.75,
};

const dueDateFieldSx = {
  "& .MuiPickersInputBase-root": {
    minHeight: 44,
    height: 44,
    paddingRight: 4,
    backgroundColor: "transparent",
  },
  "& .MuiPickersOutlinedInput-root": {
    minHeight: 44,
    height: 44,
    backgroundColor: "transparent",
  },
  "& .MuiPickersOutlinedInput-notchedOutline": {
    border: "0 !important",
  },
  "& .MuiInputBase-input": {
    color: BRAND_BLUE,
    fontWeight: 600,
    accentColor: BRAND_BLUE,
    padding: "10px 14px",
  },
  "& .MuiPickersSectionList-root": {
    color: BRAND_BLUE,
    fontWeight: 600,
    padding: "10px 14px",
    backgroundColor: "transparent",
    borderRadius: 0,
    opacity: 1,
  },
  "& .MuiPickersInputBase-sectionsContainer": {
    backgroundColor: "transparent",
    borderRadius: 0,
    opacity: 1,
  },
  "& .MuiPickersSectionList-section": {
    color: BRAND_BLUE,
    backgroundColor: "transparent",
    opacity: "1 !important",
  },
  "& .MuiPickersInputBase-sectionContent": {
    opacity: "1 !important",
  },
  "& .MuiPickersInputBase-sectionBefore, & .MuiPickersInputBase-sectionAfter":
    {
      opacity: "1 !important",
    },
  "& .MuiInputBase-input::placeholder": {
    color: BRAND_BLUE,
    opacity: 1,
  },
  "& .MuiInputAdornment-root": {
    mr: 0,
    backgroundColor: "transparent",
  },
  "& .MuiPickersInputBase-separator": {
    color: BRAND_BLUE,
  },
  "& .MuiIconButton-root": {
    color: BRAND_BLUE,
    padding: 1,
  },
  "& .MuiSvgIcon-root": {
    color: BRAND_BLUE,
  },
};

const dueDateShellSx = {
  borderRadius: 2,
  backgroundColor: FIELD_BG,
  border: `1.5px solid ${BRAND_BLUE}`,
  minHeight: 44,
  height: 44,
  overflow: "hidden",
  display: "flex",
  alignItems: "center",
  "&:focus-within": {
    borderColor: BRAND_BLUE,
    borderWidth: 2,
  },
};

const dueDatePickerPopperSx = {
  "& .MuiPaper-root": {
    border: `2px solid ${BRAND_BLUE}`,
    borderRadius: 2,
  },
  "& .MuiPickersCalendarHeader-label": {
    color: BRAND_BLUE,
    fontWeight: 700,
  },
  "& .MuiPickersArrowSwitcher-button": {
    color: BRAND_BLUE,
  },
  "& .MuiDayCalendar-weekDayLabel": {
    color: BRAND_BLUE,
    fontWeight: 700,
  },
  "& .MuiPickersDay-root": {
    color: BRAND_BLUE,
    fontWeight: 600,
  },
  "& .MuiPickersDay-root.Mui-selected": {
    backgroundColor: BRAND_BLUE,
    color: "#ffffff",
  },
  "& .MuiPickersDay-root.Mui-selected:hover": {
    backgroundColor: BRAND_BLUE,
  },
  "& .MuiPickersDay-root.MuiPickersDay-today": {
    border: `1px solid ${BRAND_BLUE}`,
  },
  "& .MuiPickersYear-yearButton.Mui-selected": {
    backgroundColor: BRAND_BLUE,
  },
  "& .MuiPickersMonth-monthButton.Mui-selected": {
    backgroundColor: BRAND_BLUE,
    color: "#ffffff",
  },
};

function TaskList() {
  const [tasks, setTasks] = useState([]);

  // Modal state
  const [companies, setCompanies] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [taskInput, setTaskInput] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [priority, setPriority] = useState("");
  const [taskType, setTaskType] = useState("");
  const [companyId, setCompanyId] = useState(null);

  // Snackbar
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const { token } = useAuth();

  // ---------- Helpers ----------
  const extractItems = useCallback((payload) => {
    return Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload?.items)
          ? payload.items
          : Array.isArray(payload?.results)
            ? payload.results
            : [];
  }, []);

  const hasMore = useCallback(
    (payload, _page, pageSize, fetchedSoFar) => {
      if (typeof payload?.has_next === "boolean") return payload.has_next;
      if (payload?.next) return true;
      if (typeof payload?.total === "number")
        return fetchedSoFar < payload.total;
      return extractItems(payload).length === pageSize;
    },
    [extractItems],
  );

  const resolveUrl = useCallback((u) => {
    if (!u || typeof u !== "string") return null;
    return /^https?:\/\//i.test(u)
      ? u
      : `${API_BASE}${u.startsWith("/") ? u : `/${u}`}`;
  }, []);

  // ---------- Fetch tasks ----------
  const fetchTasks = () => {
    const controller = new AbortController();
    api
      .get("/tasks/", { signal: controller.signal })
      .then((res) => setTasks(res.data || []))
      .catch((err) => {
        if (!isCancel(err)) console.error("Error fetching tasks", err);
      });
    return () => controller.abort();
  };

  useEffect(() => {
    if (!token) return;
    const cancel = fetchTasks();
    return () => cancel?.();
  }, [token]);

  // ---------- Fetch companies ----------
  useEffect(() => {
    if (!token) return;
    const controller = new AbortController();

    const fetchAllUserCompanies = async () => {
      const pageSize = 200;
      let offset = 0;
      let nextUrl = `/user-companies/?limit=${pageSize}&offset=${offset}`;
      let all = [];

      try {
        while (true) {
          const res = await api.get(nextUrl, { signal: controller.signal });
          const payload = res.data;
          const items = extractItems(payload);

          all.push(
            ...items
              .map((c) => ({
                id: String(c.id ?? c.company_id ?? ""),
                name: c.name ?? c.company_name ?? "",
              }))
              .filter((c) => c.id && c.name),
          );

          if (!hasMore(payload, 0, pageSize, all.length)) break;

          nextUrl = payload.next
            ? resolveUrl(payload.next)
            : `/user-companies/?limit=${pageSize}&offset=${(offset +=
                pageSize)}`;
        }

        setCompanies(all);
      } catch (err) {
        if (!isCancel(err)) console.error("Company fetch error", err);
      }
    };

    fetchAllUserCompanies();
    return () => controller.abort();
  }, [token, extractItems, hasMore, resolveUrl]);

  // ---------- Complete task ----------
  const completeTask = (id) => {
    setTasks((prev) => prev.filter((t) => t.id !== id));
    api.delete(`/tasks/${id}`).catch(() => fetchTasks());
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "High":
        return "#d32f2f";
      case "Medium":
        return "#fbc02d";
      case "Low":
        return "#388e3c";
      default:
        return "#ccc";
    }
  };

  // ---------- Add Task ----------
  const handleAddTask = () => {
    if (!taskInput.trim()) return;

    const payload = {
      text: taskInput.trim(),
      due_date: dueDate || null,
      priority,
      task_type: taskType || null,
      company_id: companyId ?? null,
    };

    api
      .post("/tasks/", payload)
      .then(() => {
        fetchTasks();
        setTaskInput("");
        setDueDate("");
        setPriority("");
        setTaskType("");
        setCompanyId(null);
        setAddOpen(false);
        setSnackbarSeverity("success");
        setSnackbarMsg("Task added.");
        setSnackbarOpen(true);
      })
      .catch(() => {
        setSnackbarSeverity("error");
        setSnackbarMsg("Could not add task.");
        setSnackbarOpen(true);
      });
  };

  return (
    <>
      <Card sx={cardBaseSx}>
        <CardContent sx={{ p: 2.5, flex: 1, display: "flex", flexDirection: "column" }}>
          {/* Card Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1.5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: 1.5,
                  background: BRAND_GREEN,
                  color: "#fff",
                  display: "grid",
                  placeItems: "center",
                  flex: "0 0 auto",
                }}
              >
                <AssignmentTurnedInIcon sx={{ fontSize: 22 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{ color: BRAND_BLUE, fontSize: 16.5, fontWeight: 700, lineHeight: 1.15 }}
                >
                  To Do List
                </Typography>
                <Typography sx={{ color: "#3a5c08", fontSize: 12.5, mt: 0.2, fontFamily: "'DM Sans', sans-serif", fontWeight: 500, letterSpacing: 0.3 }}>
                  Your upcoming tasks
                </Typography>
              </Box>
            </Box>

            <Button
              size="small"
              variant="outlined"
              startIcon={<AddTaskIcon />}
              onClick={() => setAddOpen(true)}
              sx={{
                minHeight: 34,
                px: 1.5,
                borderRadius: 2,
                border: `1px solid rgba(100,131,21,0.35)`,
                color: BRAND_GREEN,
                backgroundColor: "#ffffff",
                textTransform: "none",
                fontWeight: 700,
                fontSize: 13.5,
                "&:hover": { backgroundColor: "rgba(100,131,21,0.05)", borderColor: "rgba(100,131,21,0.6)" },
              }}
            >
              Add Task
            </Button>
          </Box>

          <Divider sx={{ mb: 1.5 }} />

          <Box sx={{ flex: 1, overflowY: "auto" }}>
            {tasks.length === 0 ? (
              <Box
                sx={{
                  flex: 1,
                  border: "1px dashed #dce4ef",
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  py: 4,
                }}
              >
                <Typography
                  sx={{ color: "#8a98b3", fontStyle: "italic", fontSize: 14 }}
                >
                  No upcoming tasks
                </Typography>
              </Box>
            ) : (
              <List>
                {tasks.map((task) => (
                  <ListItem
                    key={task.id}
                    sx={{
                      borderLeft: `6px solid ${getPriorityColor(
                        task.priority,
                      )}`,
                      mb: 1,
                      borderRadius: 2,
                    }}
                  >
                    <Checkbox
                      onChange={() => completeTask(task.id)}
                      icon={<RadioButtonUncheckedIcon />}
                      checkedIcon={<CheckCircleIcon />}
                      sx={{
                        color: "rgba(0,51,108,0.38)",
                        "&.Mui-checked": { color: BRAND_GREEN },
                        "&:hover": {
                          backgroundColor: "rgba(100,131,21,0.08)",
                        },
                      }}
                    />
                    <ListItemText
                      disableTypography
                      primary={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography fontWeight={700} color={BRAND_BLUE}>
                            {task.text}
                          </Typography>
                          {task.task_type && (
                            <Chip
                              size="small"
                              label={
                                task.task_type === "bre"
                                  ? "BR&E"
                                  : "Prospecting"
                              }
                              sx={{
                                bgcolor: "rgba(100,131,21,0.12)",
                                color: BRAND_GREEN,
                                fontWeight: 600,
                              }}
                            />
                          )}
                        </Stack>
                      }
                      secondary={
                        <>
                          {task.company_name && (
                            <Typography
                              variant="body2"
                              sx={{
                                color: BRAND_BLUE,
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                fontWeight: 600,
                              }}
                            >
                              <BusinessIcon fontSize="small" sx={{ color: BRAND_GREEN }} />
                              {task.company_name}
                            </Typography>
                          )}
                          {task.due_date && (
                            <Typography
                              variant="body2"
                              sx={{
                                color: BRAND_BLUE,
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                fontWeight: 600,
                              }}
                            >
                              <CalendarTodayIcon fontSize="small" sx={{ color: BRAND_GREEN }} />
                              Due{" "}
                              {dayjs(task.due_date).format("MMM D, YYYY")}
                            </Typography>
                          )}
                        </>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* ===== ADD TASK MODAL ===== */}
      <Dialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 2,
            border: `1.5px solid ${BRAND_BLUE}`,
            boxShadow: "0 20px 60px rgba(15, 23, 42, 0.15)",
            background: "#ffffff",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: "1px solid #dce4ef",
            px: 3,
            py: 2,
            background: "#ffffff",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: 1.5,
                background: BRAND_BLUE,
                color: "#fff",
                display: "grid",
                placeItems: "center",
                flex: "0 0 auto",
              }}
            >
              <AddTaskIcon sx={{ fontSize: 22 }} />
            </Box>
            <Box>
              <Typography
                sx={{ color: BRAND_GREEN, fontSize: 17.5, fontWeight: 700, lineHeight: 1.15 }}
              >
                Add Task
              </Typography>
              <Typography sx={{ color: "#7b8aaa", fontSize: 12.5, mt: 0.15 }}>
                Create a new task with priority and due date
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ px: 3, py: 2.5 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Stack spacing={2.5} mt={0.5}>
              <Box>
                <Typography sx={modalFieldLabelSx}>Task Description</Typography>
                <TextField
                  multiline
                  rows={4}
                  placeholder="Describe Task"
                  value={taskInput}
                  onChange={(e) => setTaskInput(e.target.value)}
                  fullWidth
                  sx={modalFieldSx}
                />
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, minmax(0, 1fr))",
                  },
                  gap: 2,
                }}
              >
                <Box>
                  <Typography sx={modalFieldLabelSx}>Due Date</Typography>
                  <Box sx={dueDateShellSx}>
                    <DatePicker
                      value={dueDate ? dayjs(dueDate) : null}
                      onChange={(value) =>
                        setDueDate(
                          value?.isValid?.() ? value.format("YYYY-MM-DD") : "",
                        )
                      }
                      format="MM/DD/YYYY"
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          sx: dueDateFieldSx,
                        },
                        popper: {
                          sx: dueDatePickerPopperSx,
                        },
                      }}
                    />
                  </Box>
                </Box>

                <Box>
                  <Typography sx={modalFieldLabelSx}>Priority</Typography>
                  <TextField
                    select
                    id="task-priority"
                    fullWidth
                    sx={modalFieldSx}
                    SelectProps={{
                      displayEmpty: true,
                      renderValue: (value) =>
                        value ? (
                          value
                        ) : (
                          <Box
                            component="span"
                            sx={{ color: BRAND_BLUE, fontStyle: "normal" }}
                          >
                            Set Task Priority
                          </Box>
                        ),
                    }}
                    value={priority}
                    onChange={(e) => setPriority(e.target.value)}
                  >
                    <MenuItem value="Low" sx={{ color: BRAND_BLUE }}>Low</MenuItem>
                    <MenuItem value="Medium" sx={{ color: BRAND_BLUE }}>Medium</MenuItem>
                    <MenuItem value="High" sx={{ color: BRAND_BLUE }}>High</MenuItem>
                  </TextField>
                </Box>

                <Box>
                  <Typography sx={modalFieldLabelSx}>Task Type</Typography>
                  <TextField
                    select
                    id="task-type"
                    fullWidth
                    sx={modalFieldSx}
                    SelectProps={{
                      displayEmpty: true,
                      renderValue: (value) =>
                        value ? (
                          value === "bre" ? "BR&E" : "Prospecting"
                        ) : (
                          <Box
                            component="span"
                            sx={{ color: BRAND_BLUE, fontStyle: "normal" }}
                          >
                            Select Task Type
                          </Box>
                        ),
                    }}
                    value={taskType}
                    onChange={(e) => setTaskType(e.target.value)}
                  >
                    <MenuItem value="bre" sx={{ color: BRAND_BLUE }}>BR&E</MenuItem>
                    <MenuItem value="prospecting" sx={{ color: BRAND_BLUE }}>Prospecting</MenuItem>
                  </TextField>
                </Box>

                <Box>
                  <Typography sx={modalFieldLabelSx}>Company</Typography>
                  <Autocomplete
                    id="task-company"
                    fullWidth
                    options={companies}
                    value={
                      companies.find((c) => c.id === String(companyId)) || null
                    }
                    onChange={(_event, value) => setCompanyId(value?.id ?? null)}
                    getOptionLabel={(option) => option?.name || ""}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    renderOption={(props, option) => (
                      <li {...props} style={{ color: BRAND_BLUE }}>
                        {option.name}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select a Company"
                        sx={modalFieldSx}
                      />
                    )}
                  />
                </Box>
              </Box>
            </Stack>
          </LocalizationProvider>
        </DialogContent>

        <DialogActions
          sx={{
            borderTop: "1px solid #dce4ef",
            px: 3,
            py: 2,
            gap: 1,
            justifyContent: "flex-end",
          }}
        >
          <Button
            onClick={() => setAddOpen(false)}
            sx={{
              color: "#5b6f91",
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<AddTaskIcon />}
            onClick={handleAddTask}
            sx={{
              backgroundColor: BRAND_GREEN,
              color: "#fff",
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 2,
              px: 2.5,
              "&:hover": { backgroundColor: "#507011" },
            }}
          >
            Save Task
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbarOpen} autoHideDuration={4000}>
        <Alert severity={snackbarSeverity}>{snackbarMsg}</Alert>
      </Snackbar>
    </>
  );
}

export default TaskList;
